/**
 * Comparison Utility Functions for v2v2 SWFL Farmers Market Application
 */

import {
  ComparisonItem,
  ComparisonMetric,
  ComparisonRow,
  ComparisonTableData,
  CompatibilityScore,
  VendorSuggestion,
} from '@/types/comparison';
import { Vendor } from '@/types/vendor';

// Default maximum items for comparison
const DEFAULT_MAX_ITEMS = 4;

/**
 * Default comparison metrics configuration
 */
export const DEFAULT_COMPARISON_METRICS: ComparisonMetric[] = [
  {
    key: 'vendorType',
    label: 'Type',
    type: 'array',
    weight: 1,
    format: (value: string[]) => value.join(', '),
  },
  {
    key: 'certifications',
    label: 'Certifications',
    type: 'array',
    weight: 2,
    format: (value: { name: string }[]) => value.map(c => c.name).join(', '),
  },
  {
    key: 'seasonalAvailability.yearRound',
    label: 'Year Round',
    type: 'boolean',
    weight: 1.5,
    format: (value: boolean) => value ? 'Yes' : 'Seasonal',
  },
  {
    key: 'farmingPractices',
    label: 'Farming Practices',
    type: 'array',
    weight: 1.5,
    format: (value: string[]) => value.length.toString(),
  },
  {
    key: 'deliveryOptions',
    label: 'Delivery Options',
    type: 'array',
    weight: 1,
    format: (value: { type: string }[]) => value.length.toString(),
  },
  {
    key: 'csaProgram.available',
    label: 'CSA Program',
    type: 'boolean',
    weight: 1.5,
    format: (value: boolean) => value ? 'Yes' : 'No',
  },
  {
    key: 'socialProof.googleRating',
    label: 'Rating',
    type: 'rating',
    weight: 2,
    format: (value: number) => value.toFixed(1),
  },
  {
    key: 'socialProof.reviewCount',
    label: 'Reviews',
    type: 'number',
    weight: 1,
    format: (value: number) => value.toLocaleString(),
  },
  {
    key: 'education.farmTours',
    label: 'Farm Tours',
    type: 'boolean',
    weight: 1,
    format: (value: boolean) => value ? 'Yes' : 'No',
  },
];

/**
 * Get a nested value from an object using a dot-notation path
 */
export function getNestedValue(obj: any, path: string): any {
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[key];
  }
  
  return current;
}

/**
 * Calculate the number of farming practices
 */
export function getFarmingPracticesCount(vendor: Vendor): number {
  return vendor.farmingPractices?.length || 0;
}

/**
 * Calculate the number of delivery options
 */
export function getDeliveryOptionsCount(vendor: Vendor): number {
  return vendor.deliveryOptions?.length || 0;
}

/**
 * Get the value for a metric from a vendor
 */
export function getMetricValue(vendor: Vendor, metric: ComparisonMetric): any {
  // Handle special cases
  if (metric.key === 'farmingPractices') {
    return getFarmingPracticesCount(vendor);
  }
  if (metric.key === 'deliveryOptions') {
    return getDeliveryOptionsCount(vendor);
  }
  
  return getNestedValue(vendor, metric.key);
}

/**
 * Format a metric value for display
 */
export function formatMetricValue(value: any, metric: ComparisonMetric): string {
  if (value === undefined || value === null) {
    return 'N/A';
  }
  
  if (metric.format) {
    return metric.format(value);
  }
  
  if (Array.isArray(value)) {
    return value.length.toString();
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  if (typeof value === 'number') {
    return value.toString();
  }
  
  return String(value);
}

/**
 * Find the best value index for a metric
 */
export function findBestValueIndex(
  values: any[],
  metric: ComparisonMetric
): number | null {
  if (values.length === 0) return null;
  
  // Filter out N/A values
  const validIndices = values
    .map((v, i) => ({ value: v, index: i }))
    .filter(({ value }) => value !== undefined && value !== null);
  
  if (validIndices.length === 0) return null;
  
  // For boolean metrics, true is better
  if (metric.type === 'boolean') {
    const trueIndices = validIndices.filter(({ value }) => value === true);
    if (trueIndices.length > 0) {
      return trueIndices[0].index;
    }
    return validIndices[0].index;
  }
  
  // For numeric metrics, higher is better (unless inverted)
  if (metric.type === 'number' || metric.type === 'rating') {
    const maxValue = Math.max(...validIndices.map(({ value }) => Number(value)));
    const bestIndex = validIndices.find(({ value }) => Number(value) === maxValue)?.index;
    return bestIndex !== undefined ? bestIndex : null;
  }
  
  // For array metrics, more items is better
  if (metric.type === 'array') {
    const maxLength = Math.max(...validIndices.map(({ value }) => Array.isArray(value) ? value.length : 0));
    const bestIndex = validIndices.find(({ value }) => 
      Array.isArray(value) && value.length === maxLength
    )?.index;
    return bestIndex !== undefined ? bestIndex : null;
  }
  
  return null;
}

/**
 * Generate comparison table data from comparison items
 */
export function generateComparisonTableData(
  items: ComparisonItem[],
  metrics: ComparisonMetric[] = DEFAULT_COMPARISON_METRICS
): ComparisonTableData {
  if (items.length === 0) {
    return { columns: [], rows: [], items: [], metrics };
  }
  
  const columns = items.map((item, index) => ({
    metric: { key: 'name', label: item.data.name, type: 'text', weight: 1 } as ComparisonMetric,
    values: [item.data],
    highlightBest: false,
  }));
  
  const rows: ComparisonRow[] = metrics.map((metric) => {
    const values = items.map((item) => getMetricValue(item.data, metric));
    const bestIndex = findBestValueIndex(values, metric) ?? undefined;

    return {
      metric,
      values,
      isHighlighted: bestIndex !== undefined,
      bestIndex,
    };
  });
  
  return {
    columns,
    rows,
    items,
    metrics,
  };
}

/**
 * Calculate compatibility score between two vendors
 */
export function calculateVendorCompatibility(
  vendor1: Vendor,
  vendor2: Vendor,
  metrics: ComparisonMetric[] = DEFAULT_COMPARISON_METRICS
): CompatibilityScore {
  const matchingMetrics: string[] = [];
  const differingMetrics: string[] = [];
  let totalWeight = 0;
  let matchWeight = 0;
  
  for (const metric of metrics) {
    const value1 = getMetricValue(vendor1, metric);
    const value2 = getMetricValue(vendor2, metric);
    
    // Normalize values for comparison
    const normalized1 = Array.isArray(value1) ? JSON.stringify(value1.sort()) : value1;
    const normalized2 = Array.isArray(value2) ? JSON.stringify(value2.sort()) : value2;
    
    const isMatch = normalized1 === normalized2;
    
    if (isMatch) {
      matchingMetrics.push(metric.label);
      matchWeight += metric.weight;
    } else {
      differingMetrics.push(metric.label);
    }
    
    totalWeight += metric.weight;
  }
  
  const score = totalWeight > 0 ? Math.round((matchWeight / totalWeight) * 100) : 0;
  
  return {
    vendorId1: vendor1.id,
    vendorId2: vendor2.id,
    score,
    matchingMetrics,
    differingMetrics,
  };
}

/**
 * Get suggestions for related vendors to compare
 */
export function getVendorSuggestions(
  targetVendor: Vendor,
  allVendors: Vendor[],
  limit: number = 3
): VendorSuggestion[] {
  const suggestions: VendorSuggestion[] = [];
  
  for (const vendor of allVendors) {
    if (vendor.id === targetVendor.id) continue;
    
    const compatibility = calculateVendorCompatibility(targetVendor, vendor);
    
    // Calculate additional scores based on shared attributes
    let additionalScore = 0;
    let reason = '';
    
    // Same vendor type
    const sharedTypes = targetVendor.vendorType.filter(t => 
      vendor.vendorType.includes(t)
    );
    if (sharedTypes.length > 0) {
      additionalScore += 10;
      reason = `Both are ${sharedTypes[0]}s`;
    }
    
    // Same area (city)
    if (targetVendor.location.city === vendor.location.city) {
      additionalScore += 5;
      reason = reason || `Both located in ${vendor.location.city}`;
    }
    
    // Similar rating
    if (Math.abs(targetVendor.socialProof.googleRating - vendor.socialProof.googleRating) < 0.5) {
      additionalScore += 5;
      reason = reason || 'Similar ratings';
    }
    
    const totalScore = compatibility.score + additionalScore;
    
    suggestions.push({
      vendor,
      score: totalScore,
      reason,
    });
  }
  
  // Sort by score descending and limit
  return suggestions
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * Sort vendors by various criteria
 */
export function sortVendors(
  vendors: Vendor[],
  criteria: 'rating' | 'reviews' | 'certifications' | 'distance',
  ascending: boolean = false
): Vendor[] {
  const sorted = [...vendors].sort((a, b) => {
    let comparison = 0;
    
    switch (criteria) {
      case 'rating':
        comparison = a.socialProof.googleRating - b.socialProof.googleRating;
        break;
      case 'reviews':
        comparison = a.socialProof.reviewCount - b.socialProof.reviewCount;
        break;
      case 'certifications':
        comparison = (a.certifications?.length || 0) - (b.certifications?.length || 0);
        break;
      case 'distance':
        // Assuming distance is a computed field
        const distA = (a as any).distance || 0;
        const distB = (b as any).distance || 0;
        comparison = distA - distB;
        break;
    }
    
    return ascending ? comparison : -comparison;
  });
  
  return sorted;
}

/**
 * Create a comparison item from a vendor
 */
export function createComparisonItem(vendor: Vendor): ComparisonItem {
  return {
    id: vendor.id,
    type: 'vendor',
    data: vendor,
  };
}

/**
 * Check if a vendor is already in comparison
 */
export function isVendorInComparison(
  vendorId: string,
  items: ComparisonItem[]
): boolean {
  return items.some(item => item.id === vendorId);
}

/**
 * Check if comparison is at maximum capacity
 */
export function isComparisonFull(
  items: ComparisonItem[],
  maxItems: number = DEFAULT_MAX_ITEMS
): boolean {
  return items.length >= maxItems;
}

/**
 * Generate a shareable comparison URL
 */
export function generateComparisonUrl(
  items: ComparisonItem[],
  baseUrl: string = '/compare'
): string {
  if (items.length === 0) return baseUrl;
  
  const vendorIds = items.map(item => item.id).join(',');
  return `${baseUrl}?ids=${encodeURIComponent(vendorIds)}`;
}

/**
 * Parse comparison IDs from URL
 */
export function parseComparisonIds(
  searchParams: URLSearchParams
): string[] {
  const idsParam = searchParams.get('ids');
  if (!idsParam) return [];
  
  return idsParam.split(',').filter(id => id.length > 0);
}

/**
 * Highlight differences between values
 */
export function highlightDifferences(
  values: any[],
  metric: ComparisonMetric
): boolean[] {
  if (values.length < 2) return values.map(() => false);
  
  const reference = values[0];
  return values.map(value => {
    if (Array.isArray(reference)) {
      return JSON.stringify(reference.sort()) !== JSON.stringify(Array.isArray(value) ? value.sort() : [value]);
    }
    return value !== reference;
  });
}

/**
 * Get metric icon based on metric type
 */
export function getMetricIcon(type: ComparisonMetric['type']): string {
  switch (type) {
    case 'array':
      return 'list';
    case 'boolean':
      return 'check-circle';
    case 'number':
      return 'hash';
    case 'rating':
      return 'star';
    case 'text':
      return 'type';
    default:
      return 'bar-chart';
  }
}
